import { NotificationType } from '@/common/enums'
import { StudyModel } from '@/models/study'
import { QuestionModel } from '@/models/question'
import { TopicModel } from '@/models/topic'
import { ForumResponseModel } from '@/models/forum/forumResponse'
import { ForumChatModel } from '@/models/forum/forumChat'
import { ForumThreadModel } from '@/models/forum/forumThread'
import { ForumResponseReaction } from '@/models/forum/forumResponseReaction'
import { StudyConversationMessageModel } from '@/models/studyConversationMessage'

export class NotificationModel {
  constructor({
    id,
    userId,
    prospectAccountId,
    type,
    sourceId,
    sourceType,
    payload = {},
    isRead = false,
    isCleared = false,
    createdAt,
    updatedAt
  }) {
    this.id = id
    this.userId = userId
    this.prospectAccountId = prospectAccountId
    this.type = type
    this.sourceId = sourceId
    this.sourceType = sourceType
    this.isRead = !!isRead
    this.isCleared = !!isCleared
    this.createdAt = createdAt ?? null
    this.updatedAt = updatedAt ?? null

    const newPayload = {}

    switch (this.type) {
      case NotificationType.STUDY_REPLY:
        newPayload.studyReply = {
          message:
            payload?.studyReply?.message && new StudyConversationMessageModel(payload?.studyReply?.message),
          study: payload?.studyReply?.study && new StudyModel(payload?.studyReply?.study),
          question: payload?.studyReply?.question && new QuestionModel(payload?.studyReply?.question),
          topic: payload?.studyReply?.topic && new TopicModel(payload?.studyReply?.topic)
        }
        break
      case NotificationType.STUDY_FOLLOW_UP:
        newPayload.studyFollowUp = {
          study: payload?.studyFollowUp?.study && new StudyModel(payload?.studyFollowUp?.study),
          questionId: payload?.studyFollowUp?.questionId || null,
          topicId: payload?.studyFollowUp?.topicId || null,
          body: payload?.studyFollowUp?.body || ''
        }
        break
      case NotificationType.CHAT_MENTION:
      case NotificationType.CHAT_REPLY:
        newPayload.chatMention = {
          response: payload?.chatMention?.response && new ForumResponseModel(payload?.chatMention?.response),
          chat: payload?.chatMention?.chat && new ForumChatModel(payload?.chatMention?.chat),
          thread: payload?.chatMention?.thread && new ForumThreadModel(payload?.chatMention?.thread),
          users: payload?.chatMention?.users,
          prospects: payload?.chatMention?.prospects
        }
        break
      case NotificationType.CHAT_RESPONSE_REACTION:
        newPayload.chatResponseReaction = {
          reaction:
            payload?.chatResponseReaction?.reaction &&
            new ForumResponseReaction(payload?.chatResponseReaction?.reaction),
          response:
            payload?.chatResponseReaction?.response &&
            new ForumResponseModel(payload?.chatResponseReaction?.response),
          chat:
            payload?.chatResponseReaction?.chat && new ForumChatModel(payload?.chatResponseReaction?.chat),
          thread:
            payload?.chatResponseReaction?.thread &&
            new ForumThreadModel(payload?.chatResponseReaction?.thread),
          users: payload?.chatResponseReaction?.users,
          prospects: payload?.chatResponseReaction?.prospects
        }
        break
    }
    this.payload = newPayload
  }

  static parseFromApi({ notification }) {
    const notificationPayload = {}

    switch (notification?.type) {
      case NotificationType.STUDY_REPLY:
        const message = StudyConversationMessageModel.parseFromApi({
          message: notification?.studyReply?.message
        })
        const study = StudyModel.parseFromApi({
          study: notification?.studyReply?.study
        })
        const question = QuestionModel.parseFromApi({
          question: notification?.studyReply?.question
        })
        const topic = TopicModel.parseFromApi({
          topic: notification?.studyReply?.topic
        })

        notificationPayload.studyReply = {
          message,
          study,
          question,
          topic
        }
        break
      case NotificationType.STUDY_FOLLOW_UP:
        notificationPayload.studyFollowUp = {
          study: StudyModel.parseFromApi({ study: notification?.studyFollowUp?.study }),
          questionId: notification?.studyFollowUp?.questionId || null,
          topicId: notification?.studyFollowUp?.topicId || null,
          body: notification?.studyFollowUp?.body || ''
        }
        break

      case NotificationType.CHAT_MENTION:
      case NotificationType.CHAT_REPLY: {
        const response = ForumResponseModel.parseFromApi({
          response: notification?.chatMention?.response,
          chatId: notification?.chatMention?.chat?.id,
          threadId: notification?.chatMention?.thread?.id,
          users: notification?.chatMention?.users,
          prospects: notification?.chatMention?.prospects
        })
        const chat = ForumChatModel.parseFromApi({
          chat: notification?.chatMention?.chat
        })
        const thread = ForumThreadModel.parseFromApi({
          thread: notification?.chatMention?.thread,
          chatId: notification?.chatMention?.chat?.id
        })

        notificationPayload.chatMention = {
          response,
          chat,
          thread,
          users: notification?.chatMention?.users,
          prospects: notification?.chatMention?.prospects
        }
        break
      }
      case NotificationType.CHAT_RESPONSE_REACTION: {
        const reaction = ForumResponseReaction.parseFromApi({
          reaction: notification?.chatResponseReaction?.reaction,
          users: notification?.chatResponseReaction?.users,
          prospects: notification?.chatResponseReaction?.prospects
        })
        const response = ForumResponseModel.parseFromApi({
          response: notification?.chatResponseReaction?.response,
          chatId: notification?.chatResponseReaction?.chat?.id,
          threadId: notification?.chatResponseReaction?.thread?.id,
          users: notification?.chatResponseReaction?.users,
          prospects: notification?.chatResponseReaction?.prospects
        })
        const chat = ForumChatModel.parseFromApi({
          chat: notification?.chatResponseReaction?.chat
        })
        const thread = ForumThreadModel.parseFromApi({
          thread: notification?.chatResponseReaction?.thread,
          chatId: notification?.chatResponseReaction?.chat?.id
        })
        notificationPayload.chatResponseReaction = {
          reaction,
          response,
          chat,
          thread,
          users: notification?.chatResponseReaction?.users,
          prospects: notification?.chatResponseReaction?.prospects
        }
        break
      }
    }

    return new NotificationModel({
      id: notification?.id,
      userId: notification?.userId,
      prospectAccountId: notification?.prospectAccountId,
      type: notification?.type,
      sourceId: notification?.sourceId,
      sourceType: notification?.sourceType,
      isRead: notification?.isRead,
      isCleared: notification?.isCleared,
      createdAt: notification?.createdAt,
      updatedAt: notification?.updatedAt,
      payload: notificationPayload
    })
  }
}
